import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  Upload, FileText, Image, FileSpreadsheet, File, 
  X, CheckCircle, AlertCircle, Download, Trash2, Loader2 
} from 'lucide-react';
import { toast } from 'sonner';

const FILE_ICONS = {
  '.pdf': { icon: FileText, color: 'text-rose-500', bg: 'bg-rose-50' },
  '.doc': { icon: FileText, color: 'text-blue-500', bg: 'bg-blue-50' },
  '.docx': { icon: FileText, color: 'text-blue-500', bg: 'bg-blue-50' },
  '.xls': { icon: FileSpreadsheet, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  '.xlsx': { icon: FileSpreadsheet, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  '.png': { icon: Image, color: 'text-blue-500', bg: 'bg-blue-50' },
  '.jpg': { icon: Image, color: 'text-blue-500', bg: 'bg-blue-50' },
  '.jpeg': { icon: Image, color: 'text-blue-500', bg: 'bg-blue-50' },
  '.gif': { icon: Image, color: 'text-blue-500', bg: 'bg-blue-50' },
  'default': { icon: File, color: 'text-slate-500', bg: 'bg-slate-50' }
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = (fileName) => {
  const ext = '.' + fileName.split('.').pop().toLowerCase();
  return FILE_ICONS[ext] || FILE_ICONS['default'];
};

export const DocumentUpload = ({ actId, documents = [], onUploadComplete, onDeleteComplete }) => {
  const { api } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFiles = async (files) => {
    const fileArray = Array.from(files);
    
    for (const file of fileArray) {
      await uploadFile(file);
    }
  };

  const uploadFile = async (file) => {
    // Validate file size (50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error(`Arquivo ${file.name} excede o limite de 50MB`);
      return;
    }

    // Validate extension
    const allowedExts = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.png', '.jpg', '.jpeg', '.gif', '.txt'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedExts.includes(ext)) {
      toast.error(`Tipo de arquivo não permitido: ${ext}`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Simulate progress (since axios doesn't support upload progress in all cases)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await api.post(`/acts/${actId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      toast.success(`${file.name} enviado com sucesso!`);
      
      if (onUploadComplete) {
        onUploadComplete(response.data);
      }

    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Erro ao enviar ${file.name}`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.document_id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erro ao baixar documento');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Excluir "${doc.file_name}"?`)) return;
    
    setDeleting(doc.document_id);
    
    try {
      await api.delete(`/documents/${doc.document_id}`);
      toast.success('Documento excluído');
      
      if (onDeleteComplete) {
        onDeleteComplete(doc.document_id);
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Erro ao excluir documento');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload Zone */}
      <div
        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
          dragActive 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        data-testid="upload-zone"
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt"
          data-testid="file-input"
        />
        
        {uploading ? (
          <div className="space-y-4">
            <Loader2 className="w-12 h-12 mx-auto text-blue-500 animate-spin" />
            <p className="text-sm text-slate-600">Enviando documento...</p>
            <Progress value={uploadProgress} className="w-64 mx-auto" />
          </div>
        ) : (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-blue-500/10 to-blue-600/20 flex items-center justify-center mb-4">
              <Upload className="w-8 h-8 text-blue-600" />
            </div>
            <p className="text-lg font-medium text-slate-800 mb-1">
              Arraste arquivos aqui
            </p>
            <p className="text-sm text-slate-500 mb-4">
              ou clique para selecionar
            </p>
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="border-blue-200 text-blue-600 hover:bg-blue-50"
            >
              <Upload className="w-4 h-4 mr-2" />
              Selecionar Arquivos
            </Button>
            <p className="text-xs text-slate-400 mt-4">
              PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, GIF, TXT • Máx. 50MB
            </p>
          </>
        )}
      </div>

      {/* Documents List */}
      {documents.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Documentos Anexados ({documents.length})
          </h4>
          
          <div className="space-y-2">
            {documents.map((doc) => {
              const { icon: Icon, color, bg } = getFileIcon(doc.file_name);
              
              return (
                <Card 
                  key={doc.document_id} 
                  className="p-3 hover:shadow-md transition-shadow"
                  data-testid={`document-${doc.document_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-5 h-5 ${color}`} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">
                        {doc.file_name}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span>{formatFileSize(doc.file_size)}</span>
                        <span>•</span>
                        <span>{new Date(doc.uploaded_at).toLocaleDateString('pt-BR')}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-500 hover:text-blue-600"
                        onClick={() => handleDownload(doc)}
                        title="Baixar"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-500 hover:text-rose-600"
                        onClick={() => handleDelete(doc)}
                        disabled={deleting === doc.document_id}
                        title="Excluir"
                      >
                        {deleting === doc.document_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;
